import React, { useState, useEffect } from 'react';

import {
  faBolt,
  faPlus,
  faEdit,
  faTrash,
  faUser,
  faFileText,
  faUserMinus,
  faComments,
  faPlug
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Paper,
  Table,
  Badge,
  Pagination,
  Group,
  Text,
  Stack,
  LoadingOverlay,
  Alert,
  Button,
  Modal,
  ActionIcon,
  Tooltip,
  Title,
  Drawer
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';

import AssignAgent from './AssignAgent';
import QuickAssignAgent from './QuickAssignAgent';
import { type Client } from './types';
import {
  SYSTEM_CONFIGS,
  useActiveIntegrations
} from '../../utils/integration';
import { MarkdownRenderer } from '../../utils/markdown';
import { PostJSON, usePostJSON } from '../../utils/request';

interface ClientResponse {
  success: boolean;
  clients: Client[];
  total: number;
  page: number;
  limit: number;
}

interface ClientListProps {
  title: string;
  isActive: boolean;
  onRefetchReady?: (refetch: () => void) => void;
  onAgentAction?: () => void;
}

const ClientList: React.FC<ClientListProps> = ({
  title, 
  isActive, 
  onRefetchReady, 
  onAgentAction
}) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [unassigningClient, setUnassigningClient] = useState<Client | null>(null);
  const [addingAgent, setAddingAgent] = useState<Client | null>(null);
  const [quickAssigning, setQuickAssigning] = useState<Client | null>(null);
  const [carePlanDrawerOpen, setCarePlanDrawerOpen] = useState(false);
  const [selectedClientForCarePlan, setSelectedClientForCarePlan] = useState<Client | null>(null);

  const activeInts = useActiveIntegrations();

  // Hook for clients
  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = usePostJSON<ClientResponse>(
    '/api/client/data', 
    {
      page, 
      limit, 
      isActive
    },
    [page, limit, isActive]
  );

  // Register refetch function with parent component
  useEffect(() => {
    if (onRefetchReady) {
      onRefetchReady(refetch);
    }
  }, [onRefetchReady, refetch]);

  const handleDeleteClient = (client: Client) => {
    setDeletingClient(client);
  };

  const handleUnassignAgent = (client: Client) => {
    setUnassigningClient(client);
  };

  const handleAddAgent = (client: Client) => {
    setAddingAgent(client);
  };

  const handleViewCarePlan = (client: Client) => {
    setSelectedClientForCarePlan(client);
    setCarePlanDrawerOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingClient) return;

    try {
      await PostJSON('/api/client/delete', {
        id: deletingClient.id
      });
      
      setDeletingClient(null);
      void refetch();
      
      notifications.show({
        title: 'Success',
        message: `Client ${deletingClient.firstName} ${deletingClient.lastName} has been deleted successfully.`,
        color: 'green'
      });
    } catch (error) {
      console.error('Error deleting client:', error);
      
      notifications.show({
        title: 'Error',
        message: 'Failed to delete client. Please try again.',
        color: 'red'
      });
    }
  };

  const handleConfirmUnassign = async () => {
    if (!unassigningClient) return;

    try {
      await PostJSON('/api/agent/remove', {
        clientId: unassigningClient.id
      });
      
      setUnassigningClient(null);
      void refetch();
      
      // Trigger refetch of all lists
      if (onAgentAction) {
        onAgentAction();
      }
      
      notifications.show({
        title: 'Success',
        message: `Phono Assistant has been unassigned from ${unassigningClient.firstName} ${unassigningClient.lastName} successfully.`,
        color: 'green'
      });
    } catch (error) {
      console.error('Error unassigning agent:', error);
      
      notifications.show({
        title: 'Error',
        message: 'Failed to unassign Phono Assistant. Please try again.',
        color: 'red'
      });
    }
  };

  const handleAgentAdded = () => {
    setAddingAgent(null);
    void refetch();

    // Trigger refetch of all lists
    if (onAgentAction) {
      onAgentAction();
    }
  };

  const handleQuickAssigned = () => {
    setQuickAssigning(null);
    void refetch();

    if (onAgentAction) {
      onAgentAction();
    }
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const clients = data?.clients || [];
  const total = data?.total || 0;
  const hasActiveIntegrations = Object.keys(activeInts).length > 0;

  if (error) {
    return (
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />
        
        <Group justify="space-between" mb="md">
          <Title order={3}>{title}</Title>
          <Badge color={isActive ? 'green' : 'gray'} variant="light">
            {total} {total === 1 ? 'client' : 'clients'}
          </Badge>
        </Group>

        {clients.length === 0 ? (
          <Text c="dimmed" py="xl" ta="center">
            No {isActive ? 'active' : 'inactive'} clients found.
          </Text>
        ) : (
          <>
            <Table highlightOnHover striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={isActive ? { base: '18%', sm: '22%', md: '22%' } : { base: '22%', sm: '28%', md: '28%' }}>Name</Table.Th>
                  <Table.Th w={isActive ? { base: '15%', sm: '18%', md: '18%' } : { base: '18%', sm: '22%', md: '22%' }}>DOB</Table.Th>
                  {isActive && (
                    <Table.Th w={{ base: '15%', sm: '18%', md: '18%' }}>Phono Number</Table.Th>
                  )}
                  {hasActiveIntegrations && (
                    <Table.Th w={{ base: '15%', sm: '15%', md: '15%' }}>Integration</Table.Th>
                  )}
                  <Table.Th w={isActive ? { base: '12%', sm: '12%', md: '12%' } : { base: '15%', sm: '15%', md: '15%' }}>Care Plan</Table.Th>
                  <Table.Th w={isActive ? { base: '25%', sm: '18%', md: '18%' } : { base: '27%', sm: '20%', md: '20%' }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {clients.map((client) => (
                  <Table.Tr key={client.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <FontAwesomeIcon icon={faUser} size="sm" />
                        <Text fw={500}>
                          {client.firstName} {client.lastName}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {client.dob ? (
                        <Text size="sm">
                          {new Date(client.dob).toISOString().split('T')[0]}
                        </Text>
                      ) : (
                        <Text c="dimmed" size="sm">No DOB</Text>
                      )}
                    </Table.Td>
                    {isActive && (
                      <Table.Td>
                        {client._agent?.phone ? (
                          <Text size="sm">
                            {client._agent.phone}
                          </Text>
                        ) : (
                          <Text c="dimmed" size="sm">No agent</Text>
                        )}
                      </Table.Td>
                    )}
                    {hasActiveIntegrations && (
                      <Table.Td>
                        {client.externalSystem && client.externalId ? (() => {
                          const systemConfig = SYSTEM_CONFIGS[client.externalSystem];
                          const integration = activeInts[client.externalSystem];
                          const linkUrl = systemConfig?.utils?.getClientLink && integration?.metadata && client.externalId
                            ? systemConfig.utils.getClientLink(integration.metadata as Record<string, string>, client.externalId)
                            : null;
                          const displayName = systemConfig?.name || client.externalSystem;
                          
                          return (
                            <Group gap="xs">
                              <FontAwesomeIcon icon={faPlug} size="sm" />
                              {linkUrl ? (
                                <Text
                                  component="a"
                                  href={linkUrl}
                                  rel="noopener noreferrer"
                                  size="sm"
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  target="_blank"
                                >
                                  {displayName}
                                </Text>
                              ) : (
                                <Text size="sm">
                                  {displayName}
                                </Text>
                              )}
                            </Group>
                          );
                        })() : (
                          <Text c="dimmed" size="sm">Not Linked</Text>
                        )}
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Tooltip label="View care plan">
                        <Button
                          color="blue"
                          leftSection={<FontAwesomeIcon icon={faFileText} size="sm" />}
                          size="xs"
                          variant="subtle"
                          onClick={() => handleViewCarePlan(client)}
                        >
                          View
                        </Button>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Edit Client">
                          <ActionIcon
                            color="blue"
                            component={Link}
                            to={`/client/edit/${client.id}`}
                            variant="subtle"
                          >
                            <FontAwesomeIcon icon={faEdit} size="sm" />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="View Encounters">
                          <ActionIcon
                            color="purple"
                            component={Link}
                            to={`/client/${client.id}/encounters`}
                            variant="subtle"
                          >
                            <FontAwesomeIcon icon={faComments} size="sm" />
                          </ActionIcon>
                        </Tooltip>
                        {!isActive && (
                          <>
                            <Tooltip label="Quick Add Assistant">
                              <ActionIcon
                                color="teal"
                                variant="subtle"
                                onClick={() => setQuickAssigning(client)}
                              >
                                <FontAwesomeIcon icon={faBolt} size="sm" />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Add Assistant">
                              <ActionIcon
                                color="green"
                                variant="subtle"
                                onClick={() => handleAddAgent(client)}
                              >
                                <FontAwesomeIcon icon={faPlus} size="sm" />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                        {isActive ? (
                          <Tooltip label="Unassign Agent">
                            <ActionIcon
                              color="orange"
                              variant="subtle"
                              onClick={() => handleUnassignAgent(client)}
                            >
                              <FontAwesomeIcon icon={faUserMinus} size="sm" />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <Tooltip label="Delete Client">
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              onClick={() => handleDeleteClient(client)}
                            >
                              <FontAwesomeIcon icon={faTrash} size="sm" />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                />
              </Group>
            )}
          </>
        )}
      </Paper>


      {/* Delete Confirmation Modal */}
      <Modal
        opened={!!deletingClient}
        size="sm"
        title="Delete Client"
        onClose={() => setDeletingClient(null)}
      >
        {deletingClient && (
          <Stack gap="md">
            <Text>
              Are you sure you want to delete{' '}
              <Text component="span" fw={500}>
                {deletingClient.firstName} {deletingClient.lastName}
              </Text>
              ? This action cannot be undone.
            </Text>
            <Group gap="sm" justify="flex-end">
              <Button variant="outline" onClick={() => setDeletingClient(null)}>
                Cancel
              </Button>
              <Button color="red" onClick={() => void handleConfirmDelete()}>
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Unassign Agent Confirmation Modal */}
      <Modal
        opened={!!unassigningClient}
        size="sm"
        title="Unassign Agent"
        onClose={() => setUnassigningClient(null)}
      >
        {unassigningClient && (
          <Stack gap="md">
            <Text>
              Are you sure you want to unassign the agent from{' '}
              <Text component="span" fw={500}>
                {unassigningClient.firstName} {unassigningClient.lastName}
              </Text>
              ? This will remove the agent&apos;s phone number from this client.
            </Text>
            <Group gap="sm" justify="flex-end">
              <Button variant="outline" onClick={() => setUnassigningClient(null)}>
                Cancel
              </Button>
              <Button color="orange" onClick={() => void handleConfirmUnassign()}>
                Unassign
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Add Agent Drawer */}
      <Drawer
        opened={!!addingAgent}
        position="right"
        size="md"
        title="Add Phono Assistant to Client"
        onClose={() => setAddingAgent(null)}
      >
        {addingAgent && (
          <AssignAgent
            client={addingAgent}
            onCancel={() => setAddingAgent(null)}
            onSuccess={handleAgentAdded}
          />
        )}
      </Drawer>

      {/* Quick Assign Agent Modal */}
      <Modal
        centered
        opened={!!quickAssigning}
        title="Quick Add Phono Assistant"
        onClose={() => setQuickAssigning(null)}
      >
        {quickAssigning && (
          <QuickAssignAgent
            client={quickAssigning}
            onCancel={() => setQuickAssigning(null)}
            onSuccess={handleQuickAssigned}
          />
        )}
      </Modal>

      {/* Care Plan Drawer */}
      <Drawer
        opened={carePlanDrawerOpen}
        position="right"
        size="lg"
        title={
          <Text fw={700}>
            {selectedClientForCarePlan ? 
              `Care Plan - ${selectedClientForCarePlan.firstName} ${selectedClientForCarePlan.lastName}` : 
              'Care Plan'}
          </Text>
        }
        onClose={() => setCarePlanDrawerOpen(false)}
      >
        {selectedClientForCarePlan && (
          <Stack gap="md">
            {selectedClientForCarePlan.carePlan ? (
              <MarkdownRenderer content={selectedClientForCarePlan.carePlan} />
            ) : (
              <Text c="dimmed" py="xl" ta="center">
                No care plan available for this client.
              </Text>
            )}
          </Stack>
        )}
      </Drawer>
    </>
  );
};

export default ClientList;
